import * as d3 from 'd3'
import { geoCentroid } from 'd3-geo'
import coordinateIcon from '@/assets/SituationRoom_scss/src/map_icon/dashboard_customize_nb.png'
import pvIcon from '@/assets/SituationRoom_scss/src/map_icon/pv_nb.png'
import bessIcon from '@/assets/SituationRoom_scss/src/map_icon/bess_nb.png'
import loadIcon from '@/assets/SituationRoom_scss/src/map_icon/load_nb.png'
import { TYPE_MAP, getProgressColor, ICON_CONFIG, LABEL_CONFIG, ANIMATION_CONFIG } from './constants'

const iconSourceMap = {
  solarInfo: pvIcon,
  mysqlInfo: bessIcon,
  powerInfo: loadIcon
}

export class IconHandler {
  constructor (iconLayer, projection, geoData, vm, state, tooltipManager, infoWindowManager) {
    this.iconLayer = iconLayer
    this.projection = projection
    this.geoData = geoData
    this.vm = vm
    this.state = state
    this.tooltipManager = tooltipManager
    this.infoWindowManager = infoWindowManager

    this.labelOverrides = new Map()
  }

  clearLabelOverrides (sites) {
    if (!sites?.length) return
    sites.forEach(site => {
      const id = site.id || `${site.county}-${site.lat}-${site.lon}`
      this.labelOverrides.delete(id)
    })
  }

  // 控制所有 site 標籤的顯示/隱藏
  updateAllLabelsVisibility () {
    const wantShow = !!this.vm.showSiteLabels
    const labels = this.iconLayer.selectAll('.site-label-group')

    labels
      .interrupt() // 取消未完成的 transition，避免 race condition
      .transition()
      .duration(ANIMATION_CONFIG.LABEL_FADE_DURATION)
      .style('opacity', wantShow ? 1 : 0)
  }

  drawParentIcon (site, isInCluster = false) {
    const siteId = site.id || `${site.county}-${site.lat}-${site.lon}`
    if (this.iconLayer.selectAll(`[data-site-id="${siteId}"]`).size()) {
      return
    }

    let lon = site.lon
    let lat = site.lat

    // 檢查初始座標是否有效
    if (isNaN(lon) || isNaN(lat) || lon === null || lat === null || lon === undefined || lat === undefined) {
      console.warn('Invalid initial coordinates for site:', site.name, 'lon:', lon, 'lat:', lat)
      return
    }

    if (site.fromGeocode && site.fromGeocode.location_type !== 'ROOFTOP') {
      if (site.fromGeocode.bounds) {
        const b = site.fromGeocode.bounds
        lon = (b.northeast.lon + b.southwest.lon) / 2
        lat = (b.northeast.lat + b.southwest.lat) / 2
      } else {
        const target = this.geoData.features.find(f => f.properties.COUNTYNAME === site.county)
        if (target) {
          const centroid = geoCentroid(target)
          if (centroid && centroid.length === 2 && !isNaN(centroid[0]) && !isNaN(centroid[1])) {
            [lon, lat] = centroid
          } else {
            console.warn('Invalid centroid for county:', site.county)
            return
          }
        } else {
          console.warn('County not found in geoData:', site.county)
          return
        }
      }
    }

    const [x, y] = this.projection([lon, lat])

    // 檢查投影結果是否有效
    if (isNaN(x) || isNaN(y)) {
      console.warn('Invalid coordinates for site:', site.name, 'lon:', lon, 'lat:', lat)
      return // 跳過無效座標的站點
    }

    let percent = site.availability || 0

    percent = Math.max(0, Math.min(100, percent))

    const iconSrc = site.types?.length === 1 ? iconSourceMap[site.types[0]] : coordinateIcon

    const radius = ICON_CONFIG.BASE_RADIUS
    const color = getProgressColor(percent)
    const iconCenter = ICON_CONFIG.ICON_CENTER
    const border = ICON_CONFIG.BORDER_WIDTH

    const iconGroup = this.iconLayer.append('g')
      .attr('class', 'icon-group parent-icon')
      .attr('data-site-id', siteId)
      .attr('transform', `translate(${x}, ${y})`)

    iconGroup.append('circle')
      .attr('class', 'progress-bg')
      .attr('cx', iconCenter)
      .attr('cy', iconCenter)
      .attr('r', radius)
      .attr('stroke', color)
      .attr('stroke-width', border)
      .attr('fill', 'none')

    iconGroup.append('image')
      .attr('xlink:href', iconSrc)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 1)
      .attr('height', 1)
      .attr('class', 'progress-bg pointer')
      .on('mouseover', (event) => this.handleParentMouseOver(event, site, percent))
      .on('click', (event) => this.handleParentClick(event, site, iconGroup, isInCluster))

    // 只有在非 cluster 狀態下才創建標籤
    if (!isInCluster) {
      this.createSmartLabel(iconGroup, site, iconCenter, radius)
    }

    return iconGroup
  }

  handleParentMouseOver (event, site, percent) {
    if (this.state.isClickingIcon) return
    this.tooltipManager.clearTimeout()

    const readableType = site.types.map(t => TYPE_MAP[t]).join('/')

    this.tooltipManager.setTooltipContent(`
      <h5>${site.name}</h5>
      <div class="tooltip_inf">
        <p>案場類型：${readableType}</p>
        <p>資料妥善率：${percent.toFixed(1)}%</p>
      </div>
    `)

    const moveHandler = (e) => this.tooltipManager.updateTooltipPosition(e, 'parent')
    document.addEventListener('mousemove', moveHandler)

    d3.select(event.currentTarget).on('mouseout', () => {
      document.removeEventListener('mousemove', moveHandler)
      this.tooltipManager.hideTooltip()
    })
  }

  handleParentClick (event, site, iconGroup, isInCluster = false) {
    event.stopPropagation()

    this.state.isClickingIcon = true
    setTimeout(() => { this.state.isClickingIcon = false }, 300)
    this.tooltipManager.hideTooltip()

    // 單一類型的資料，點擊時立即顯示資訊窗
    if (site.types?.length === 1) {
      this.vm.currentPageIdx = 0
      this.openSiteInfo(site)

      return
    }

    const expanded = iconGroup.attr('data-expanded') === 'true'
    if (!expanded) {
      this.expandParentIcon(iconGroup, site, isInCluster)

      // 若要點選 parent icon 時立即呈現案場第一個類型資料, 這裡打開
      // this.vm.currentPageIdx = 0
      // this.openSiteInfo(site)
    } else {
      this.collapseAllIcons(site)
    }
  }

  expandParentIcon (iconGroup, site, isInCluster = false) {
    this.iconLayer.selectAll('.progress-bg').classed('image-focus-shadow', false)
    this.iconLayer.selectAll('image').attr('data-expanded', null)
    this.iconLayer.selectAll('[data-site-id]').attr('data-expanded', null)
    this.toggleProgressBgFocus(site, true)

    if (this.state.hasChildIcons) {
      this.collapseChildIcons(site)
      setTimeout(() => {
        this.infoWindowManager.fadeOutInfoWindow()
        iconGroup.attr('data-expanded', true)
        this.expandChildIcons(site, isInCluster)
        this.updateAllLabelsVisibility()
      }, 300)
    } else {
      this.iconLayer.selectAll('.child-icon').remove()
      this.iconLayer.selectAll('.child-outline').remove()
      this.infoWindowManager.fadeOutInfoWindow()
      iconGroup.attr('data-expanded', true)
      this.expandChildIcons(site, isInCluster)
      this.updateAllLabelsVisibility()
    }
  }

  collapseAllIcons (site) {
    this.iconLayer.selectAll('image').attr('data-expanded', null)
    this.iconLayer.selectAll('[data-site-id]').attr('data-expanded', null)
    this.toggleProgressBgFocus(site, null)

    this.collapseChildIcons(site)

    this.infoWindowManager.fadeOutInfoWindow()
    this.state.hasChildIcons = false

    this.updateAllLabelsVisibility()
  }

  toggleProgressBgFocus (site, isFocused) {
    const siteId = site.id || `${site.county}-${site.lat}-${site.lon}`
    this.iconLayer.selectAll(`[data-site-id="${siteId}"] .progress-bg`)
      .classed('image-focus-shadow', isFocused)
  }
  expandChildIcons (site, isInCluster = false) {
    if (site.types?.length <= 1) return

    const siteId = site.id || `${site.county}-${site.lat}-${site.lon}`
    const parentIcon = this.iconLayer.selectAll(`[data-site-id="${siteId}"]`)

    if (parentIcon.empty()) return

    const parentTransform = parentIcon.attr('transform')
    let px, py

    if (parentTransform) {
      const match = parentTransform.match(/translate\(([^,]+),([^)]+)\)/)
      if (match) {
        px = parseFloat(match[1]) + 2
        py = parseFloat(match[2]) + 2
      } else {
        [px, py] = this.projection([site.lon, site.lat])
        px += 2
        py += 2
      }
    } else {
      [px, py] = this.projection([site.lon, site.lat])
      px += 2
      py += 2
    }

    this.iconLayer.selectAll('.child-icon').remove()
    this.iconLayer.selectAll('.child-outline').remove()

    const iconSize = 1
    const childIconSize = 4
    const padding = 2
    const baseRadius = (iconSize + childIconSize) / 2 + padding
    const distRadius = baseRadius + (site.types?.length >= 3 ? 2 : 0)

    const angleStep = site.types?.length > 1 ? (2 * Math.PI) / site.types?.length : 0
    const startAngle = -Math.PI / 2
    // 當有三種類型時，固定在 上(-90°)、右(0°)、下(90°)
    const fixedAnglesForThree = [-Math.PI / 2, 0, Math.PI / 2]

    const childIcons = []

    site.types.forEach((type, i) => {
      const angle = site.types?.length === 3
        ? fixedAnglesForThree[i % 3]
        : (startAngle + i * angleStep)
      const childX = px + distRadius * Math.cos(angle)
      const childY = py + distRadius * Math.sin(angle)
      const iconSrc = iconSourceMap[type] || ''

      const outline = this.iconLayer.append('circle')
        .attr('class', 'child-outline')
        .attr('cx', px)
        .attr('cy', py)
        .attr('r', 0)
        .attr('stroke', '#FFFFFF')
        .attr('stroke-width', 0.6)
        .attr('fill', 'transparent')
        .attr('opacity', 0)

      const image = this.iconLayer.append('image')
        .attr('xlink:href', iconSrc)
        .attr('class', 'child-icon pointer image-hover-shadow')
        .attr('data-site-type', type)
        .attr('x', px - childIconSize / 2)
        .attr('y', py - childIconSize / 2)
        .attr('width', 0)
        .attr('height', 0)
        .attr('opacity', 0)

      childIcons.push({
        element: image.node(),
        outlineElement: outline.node(),
        targetCx: childX,
        targetCy: childY,
        targetR: childIconSize / 2,
        targetX: childX - childIconSize / 2,
        targetY: childY - childIconSize / 2,
        targetWidth: childIconSize,
        targetHeight: childIconSize,
        type
      })
    })

    childIcons.forEach((childIcon, i) => {
      const { element, outlineElement, targetX, targetY, targetWidth, targetHeight, targetCx, targetCy, targetR, type } = childIcon
      const d3Element = d3.select(element)
      const d3Outline = d3.select(outlineElement)

      d3Element
        .on('mouseover', (event) => this.handleChildMouseOver(event, type))
        .on('click', (event) => this.handleChildClick(event, site, type))

      const delayTime = i * 100

      d3Element.transition()
        .duration(400)
        .delay(delayTime)
        .ease(d3.easeCubicOut)
        .attr('x', targetX)
        .attr('y', targetY)
        .attr('width', targetWidth)
        .attr('height', targetHeight)
        .style('opacity', 1)

      d3Outline.transition()
        .duration(400)
        .delay(delayTime)
        .ease(d3.easeCubicOut)
        .attr('cx', targetCx)
        .attr('cy', targetCy)
        .attr('r', targetR)
        .style('opacity', 1)
    })

    if (!isInCluster && this.state.showLabels) {
      this.showSmartLabels([site])
    }

    this.state.hasChildIcons = true
  }

  collapseChildIcons (site) {
    const siteId = site.id || `${site.county}-${site.lat}-${site.lon}`
    const parentIcon = this.iconLayer.selectAll(`[data-site-id="${siteId}"]`)

    let px, py

    if (parentIcon.size()) {
      const parentTransform = parentIcon.attr('transform')

      if (parentTransform) {
        const match = parentTransform.match(/translate\(([^,]+),([^)]+)\)/)
        if (match) {
          px = parseFloat(match[1]) + 2
          py = parseFloat(match[2]) + 2
        } else {
          [px, py] = this.projection([site.lon, site.lat])
          px += 2
          py += 2
        }
      } else {
        [px, py] = this.projection([site.lon, site.lat])
        px += 2
        py += 2
      }
    } else {
      [px, py] = this.projection([site.lon, site.lat])
    }

    const childIconElements = this.iconLayer.selectAll('.child-icon').nodes()
    const childOutlineElements = this.iconLayer.selectAll('.child-outline').nodes()

    childIconElements.forEach((childIconElement, i) => {
      const childIcon = d3.select(childIconElement)
      const currentWidth = parseFloat(childIcon.attr('width'))
      const currentHeight = parseFloat(childIcon.attr('height'))

      const safeWidth = isNaN(currentWidth) ? 0 : Math.max(0, currentWidth)
      const safeHeight = isNaN(currentHeight) ? 0 : Math.max(0, currentHeight)

      const delayTime = i * 50

      childIcon.transition()
        .duration(300)
        .delay(delayTime)
        .ease(d3.easeCubicIn)
        .attr('x', px - safeWidth / 2)
        .attr('y', py - safeHeight / 2)
        .attr('width', 0)
        .attr('height', 0)
        .style('opacity', 0)
        .on('end', function () {
          d3.select(this).remove()
        })
    })

    childOutlineElements.forEach((outlineElement, i) => {
      const outline = d3.select(outlineElement)
      const delayTime = i * 50
      outline.transition()
        .duration(300)
        .delay(delayTime)
        .ease(d3.easeCubicIn)
        .attr('cx', px)
        .attr('cy', py)
        .attr('r', 0)
        .style('opacity', 0)
        .on('end', function () {
          d3.select(this).remove()
        })
    })

    this.updateAllLabelsVisibility()
  }

  handleChildMouseOver (event, type) {
    this.tooltipManager.setTooltipContent(`<div class="childIconType">${TYPE_MAP[type]}</div>`, 70)
    const moveHandler = (e) => this.tooltipManager.updateTooltipPosition(e, 'child')
    document.addEventListener('mousemove', moveHandler)

    d3.select(event.currentTarget).on('mouseout', () => {
      document.removeEventListener('mousemove', moveHandler)
      this.tooltipManager.hideTooltip()
    })
  }

  handleChildClick (event, site, type) {
    event.stopPropagation()

    this.vm.currentPageIdx = site.types.indexOf(type) // 點選的 type 為 types 陣列第幾個
    this.openSiteInfo(site)
  }

  openSiteInfo (site) {
    this.vm.changedSiteName = this.vm.clickedSiteInfo?.name !== site.name

    this.vm.isInfoWindowDataLoading = true

    this.vm.clickedSiteInfo = site
    this.vm.getAndShowData()

    this.infoWindowManager.fadeInInfoWindow(site)
  }

  applyForceSimulation (allParentIcons) {
    if (allParentIcons.length <= 1) return

    const iconData = allParentIcons.map((iconGroup, index) => {
      const transform = iconGroup.attr('transform')
      const translateMatch = transform.match(/translate\(([^,]+),\s*([^)]+)\)/)
      return {
        iconGroup,
        x: translateMatch ? parseFloat(translateMatch[1]) : 0,
        y: translateMatch ? parseFloat(translateMatch[2]) : 0,
        index
      }
    })

    const simulation = d3.forceSimulation(iconData)
      .force('collide', d3.forceCollide(6))
      .force('x', d3.forceX(d => d.x).strength(0.12))
      .force('y', d3.forceY(d => d.y).strength(0.12))
      .on('tick', () => {
        iconData.forEach(d => {
          d.iconGroup.attr('transform', `translate(${d.x}, ${d.y})`)
        })
      })
      .stop()

    simulation.alpha(1).restart()
    setTimeout(() => simulation.stop(), 1000)
  }

  createSmartLabel (iconGroup, site, iconCenter, radius) {
    const siteId = site.id || `${site.county}-${site.lat}-${site.lon}`

    const labelGroup = iconGroup.append('g')
      .attr('class', 'site-label-group')
      .attr('data-site-id', siteId)

    const labelBg = labelGroup.append('rect').attr('class', 'label-background')
    const labelText = labelGroup.append('text').attr('class', 'label-text').text(site.name)

    setTimeout(() => {
      const position = this.calculateOptimalLabelPosition(iconGroup, site, iconCenter, radius)
      this.positionLabel(labelGroup, position, labelBg, labelText)
    }, 100)
  }

  calculateOptimalLabelPosition (iconGroup, site, iconCenter, radius) {
    const currentTransform = this.iconLayer.node().ownerSVGElement?.querySelector('g')?.getAttribute('transform')
    let scale = 1
    if (currentTransform) {
      const scaleMatch = currentTransform.match(/scale\(([^)]+)\)/)
      if (scaleMatch) scale = parseFloat(scaleMatch[1])
    }

    const baseDistance = LABEL_CONFIG.BASE_DISTANCE
    const adjustedScale = Math.max(scale, 0.3)
    const minDistance = LABEL_CONFIG.MIN_DISTANCE
    const distance = Math.max(baseDistance / adjustedScale, minDistance / adjustedScale)

    const siteId = site.id || `${site.county}-${site.lat}-${site.lon}`
    const override = this.labelOverrides.get(siteId)
    if (override && override.direction) {
      const dir = override.direction
      const dist = Math.max(distance, (override.distance || distance))
      const r = radius + dist
      const diag = r * 0.707
      const positions = {
        'right': { name: 'right', angle: 0, x: iconCenter + r, y: iconCenter, anchor: 'start' },
        'top-right': { name: 'top-right', angle: 45, x: iconCenter + diag, y: iconCenter - diag, anchor: 'start' },
        'top': { name: 'top', angle: 90, x: iconCenter, y: iconCenter - r, anchor: 'middle' },
        'top-left': { name: 'top-left', angle: 135, x: iconCenter - diag, y: iconCenter - diag, anchor: 'end' },
        'left': { name: 'left', angle: 180, x: iconCenter - r, y: iconCenter, anchor: 'end' },
        'bottom-left': { name: 'bottom-left', angle: 225, x: iconCenter - diag, y: iconCenter + diag, anchor: 'end' },
        'bottom': { name: 'bottom', angle: 270, x: iconCenter, y: iconCenter + r, anchor: 'middle' },
        'bottom-right': { name: 'bottom-right', angle: 315, x: iconCenter + diag, y: iconCenter + diag, anchor: 'start' }
      }
      const pos = positions[dir] || positions['bottom']
      if (typeof override.offsetY === 'number') {
        pos.y += override.offsetY
      }
      return pos
    }

    const position = {
      name: 'bottom',
      angle: 270,
      x: iconCenter,
      y: iconCenter + radius + distance,
      anchor: 'middle'
    }

    return position
  }

  positionLabel (labelGroup, position, labelBg, labelText) {
    labelText
      .attr('x', position.x)
      .attr('y', position.y)
      .attr('text-anchor', position.anchor)

    let bgTextWidth = 0
    try {
      const textContent = labelText && typeof labelText.text === 'function' ? labelText.text() : ''
      bgTextWidth = (textContent || '').length * 0.5
    } catch {
      bgTextWidth = 0
    }

    const bgTextHeight = LABEL_CONFIG.TEXT_HEIGHT
    const padding = LABEL_CONFIG.PADDING

    labelBg
      .attr('x', position.x - bgTextWidth / 2 - padding)
      .attr('y', position.y - bgTextHeight / 2 - padding)
      .attr('width', bgTextWidth + padding * 2)
      .attr('height', bgTextHeight + padding * 2)
  }

  showSmartLabels (sites) {
    sites.forEach(site => {
      const siteId = site.id || `${site.county}-${site.lat}-${site.lon}`
      const labelGroup = this.iconLayer.selectAll(`[data-site-id="${siteId}"] .site-label-group`)

      if (labelGroup.size()) {
        labelGroup.transition()
          .duration(ANIMATION_CONFIG.LABEL_FADE_DURATION)
          .delay(ANIMATION_CONFIG.STAGGER_DELAY)
          .style('opacity', 1)
      }
    })
  }

  hideSmartLabels (sites) {
    if (sites && sites.length > 0) {
      sites.forEach(site => {
        const siteId = site.id || `${site.county}-${site.lat}-${site.lon}`
        const labelGroup = this.iconLayer.selectAll(`[data-site-id="${siteId}"] .site-label-group`)

        if (labelGroup.size()) {
          labelGroup.transition()
            .duration(ANIMATION_CONFIG.LABEL_HIDE_DURATION)
            .style('opacity', 0)
        }
      })
    } else {
      this.iconLayer.selectAll('.site-label-group')
        .transition()
        .duration(ANIMATION_CONFIG.LABEL_HIDE_DURATION)
        .style('opacity', 0)
    }
  }
}
